import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import {
  Loader2, CheckCircle2, ChevronLeft, ChevronRight, FileText,
  Eraser, IdCard, User, Camera, Image as ImageIcon,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { toast } from '../../../hooks/use-toast';
import AudioInstruction from '../AudioInstruction';

const STEPS = ['Bon papier', 'CNI', 'Nom', 'Signature'];

/**
 * PM Spec V3 13/05/2026 — PaperBonFlow
 *
 * Flow alternatif quand le bon papier est déjà signé sur place :
 *   1. Photo du bon papier signé (recto)
 *   2. Photo de la CNI du signataire
 *   3. Saisie / validation du nom du signataire (préfilé si lien)
 *   4. Signature DIGITALE du chauffeur (qui atteste de la transcription)
 *
 * Le `party` peut être 'shipper' ou 'consignee' selon le bon.
 */
const compress = (file, maxW = 1280, q = 0.7) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const ratio = Math.min(1, maxW / img.width);
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * ratio);
    c.height = Math.round(img.height * ratio);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    const dataUrl = c.toDataURL('image/jpeg', q);
    resolve({ dataUrl, fileSize: Math.round(dataUrl.length * 0.75) });
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('IMG_ERROR')); };
  img.src = url;
});

const PhotoStep = ({ stepIdx, label, audio, photo, onPhoto, onRemove, testIdPrefix }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const r = await compress(f);
      onPhoto?.({ ...r, source: 'camera_live' });
    } catch {
      toast({ title: 'Photo non chargée', variant: 'destructive' });
    }
  };
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Badge className="bg-emerald-600 text-white border-0">Étape {stepIdx} / 4</Badge>
        <div className="text-xl font-bold text-slate-900 leading-tight">{label}</div>
        {audio && <AudioInstruction text={audio} testId={`${testIdPrefix}-audio`} />}
      </div>
      {photo ? (
        <div className="relative">
          <img src={photo.dataUrl} alt="" className="w-full h-56 object-cover rounded-lg border border-slate-200" />
          <Button size="sm" variant="destructive" data-testid={`${testIdPrefix}-remove`}
                  onClick={onRemove} className="absolute top-2 right-2 h-7">
            Refaire
          </Button>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                 onChange={handle} className="hidden" data-testid={`${testIdPrefix}-camera`} />
          <input ref={galleryRef} type="file" accept="image/*"
                 onChange={handle} className="hidden" data-testid={`${testIdPrefix}-gallery`} />
          <Button size="lg" onClick={() => cameraRef.current?.click()}
                  data-testid={`${testIdPrefix}-camera-btn`}
                  className="flex-1 h-14 bg-emerald-600 hover:bg-emerald-700 gap-2">
            <Camera className="w-5 h-5" /> Prendre maintenant
          </Button>
          <Button size="lg" variant="outline" onClick={() => galleryRef.current?.click()}
                  data-testid={`${testIdPrefix}-gallery-btn`}
                  className="flex-1 h-14 gap-2">
            <ImageIcon className="w-5 h-5" /> Galerie
          </Button>
        </div>
      )}
    </div>
  );
};

const PaperBonFlow = ({
  type, party, operation, operatorName, preFillName,
  endpointPrefix, authHeaders, linkId, noteId,
  onCompleted,
}) => {
  // Bug fix 16/02/2026 — Stabilise axios.create via useState lazy init
  const [ax] = useState(() => axios.create({ headers: authHeaders || {} }));
  const [step, setStep] = useState(0);
  const [photoBon, setPhotoBon] = useState(null);
  const [photoCNI, setPhotoCNI] = useState(null);
  const [signerName, setSignerName] = useState(preFillName || '');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gps, setGps] = useState(null);
  const sigRef = useRef(null);
  const noteRef = useRef(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { timeout: 5000 }
    );
  }, []);

  // PM Bug fix 17/02/2026 — Priorité au noteId scopé (JWT) en multi-rotation
  const ensureNote = useCallback(async () => {
    if (noteRef.current) return noteRef.current;
    const list = await ax.get(endpointPrefix);
    const allNotes = list.data || [];
    let existing;
    if (noteId) existing = allNotes.find(n => n.id === noteId);
    if (!existing && linkId) {
      existing = allNotes.find(n =>
        (n.signatureLinks || []).some(l => l.id === linkId)
      );
    }
    if (!existing) existing = allNotes.find(n => n.type === type);
    if (existing) { noteRef.current = existing; return existing; }
    const created = await ax.post(endpointPrefix, { type });
    noteRef.current = created.data;
    return created.data;
  }, [endpointPrefix, type, ax, linkId, noteId]);

  useEffect(() => { ensureNote().catch(() => {}); }, [ensureNote]);

  const submit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature chauffeur requise', variant: 'destructive' });
      return;
    }
    if (!signerName || signerName.length < 2) {
      toast({ title: 'Nom du signataire papier requis', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const note = await ensureNote();
      const noteId = note.id;
      // 1) Photo bon papier
      let bonPhotoId = null;
      if (photoBon) {
        const r = await ax.post(`${endpointPrefix}/${noteId}/photos`, {
          label: type === 'loading' ? 'bon_papier' : 'bon_papier_signe',
          base64: photoBon.dataUrl, fileSize: photoBon.fileSize,
          source: photoBon.source || 'camera_live',
          replaceByLabel: true,   // Bug fix 16/02 — idempotence re-tries
        });
        bonPhotoId = r.data?.photos?.[r.data.photos.length - 1]?.id || null;
      }
      // 2) Photo CNI
      let cniPhotoId = null;
      if (photoCNI) {
        const r = await ax.post(`${endpointPrefix}/${noteId}/photos`, {
          label: type === 'loading' ? 'cni_signataire' : 'cni_destinataire',
          base64: photoCNI.dataUrl, fileSize: photoCNI.fileSize,
          source: photoCNI.source || 'camera_live',
          replaceByLabel: true,   // Bug fix 16/02 — idempotence re-tries
        });
        cniPhotoId = r.data?.photos?.[r.data.photos.length - 1]?.id || null;
      }
      // 3) Signature site_party (basée sur le bon papier — channel paper_signed_photo)
      // On pose la signature pour shipper/consignee avec image = photo du bon
      if (photoBon) {
        await ax.post(`${endpointPrefix}/${noteId}/sign`, {
          party,
          name: signerName,
          signatureBase64: photoBon.dataUrl,  // image du bon papier comme preuve,
          gpsCoords: gps,
          channel: 'paper_signed_photo',
          idDocumentType: 'cni',
          idPhotoId: cniPhotoId,
          linkId: linkId || undefined,
        });
      }
      // 4) Signature DIGITALE du chauffeur (atteste)
      const driverSig = sigRef.current.getCanvas().toDataURL('image/png');
      await ax.post(`${endpointPrefix}/${noteId}/sign`, {
        party: 'driver',
        name: operatorName || 'Chauffeur',
        signatureBase64: driverSig,
        gpsCoords: gps,
        channel: 'driver_device',
      });
      setSuccess(true);
      onCompleted?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur';
      toast({ title: 'Échec', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="paperbon-success">
        <CardContent className="p-8 text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-white" />
          </div>
          <div className="text-xl font-bold text-emerald-800">Bon papier transmis !</div>
          <div className="text-sm text-slate-600">Photos et signature enregistrées.</div>
        </CardContent>
      </Card>
    );
  }

  const Stepper = () => (
    <div className="flex items-center gap-1 mb-3" data-testid="paperbon-stepper">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            i <= step ? 'bg-emerald-600 text-white' : 'bg-slate-200'
          }`}>
            {i < step ? '✓' : i + 1}
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  if (step === 0) {
    return (
      <Card data-testid="paperbon-step-0">
        <CardContent className="p-5 space-y-4">
          <Stepper />
          <PhotoStep
            stepIdx={1}
            label="Photo du bon papier signé (recto)"
            audio="Photographiez le bon papier signé en entier"
            photo={photoBon}
            onPhoto={setPhotoBon}
            onRemove={() => setPhotoBon(null)}
            testIdPrefix="paperbon-bon"
          />
          <div className="flex justify-end pt-2">
            <Button size="lg" disabled={!photoBon} onClick={() => setStep(1)}
                    data-testid="paperbon-step0-next"
                    className="bg-emerald-600 hover:bg-emerald-700">
              Continuer <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 1) {
    return (
      <Card data-testid="paperbon-step-1">
        <CardContent className="p-5 space-y-4">
          <Stepper />
          <PhotoStep
            stepIdx={2}
            label="Photo de la CNI du signataire"
            audio="Photographiez la CNI ou la pièce d'identité"
            photo={photoCNI}
            onPhoto={setPhotoCNI}
            onRemove={() => setPhotoCNI(null)}
            testIdPrefix="paperbon-cni"
          />
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(0)}
                    data-testid="paperbon-step1-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button size="lg" disabled={!photoCNI} onClick={() => setStep(2)}
                    data-testid="paperbon-step1-next"
                    className="bg-emerald-600 hover:bg-emerald-700">
              Continuer <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 2) {
    return (
      <Card data-testid="paperbon-step-2">
        <CardContent className="p-5 space-y-4">
          <Stepper />
          <div className="space-y-2">
            <Badge className="bg-emerald-600 text-white border-0">Étape 3 / 4</Badge>
            <div className="text-xl font-bold text-slate-900">Nom du signataire papier</div>
            <AudioInstruction text="Tapez le nom de la personne qui a signé le bon papier" testId="paperbon-step2-audio" />
          </div>
          <Input
            data-testid="paperbon-name-input"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Ex: M. Diabaté"
            className="h-14 text-lg"
          />
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setStep(1)}
                    data-testid="paperbon-step2-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <Button size="lg" disabled={!signerName || signerName.length < 2}
                    onClick={() => setStep(3)} data-testid="paperbon-step2-next"
                    className="bg-emerald-600 hover:bg-emerald-700">
              Continuer <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Step 3 : signature digitale chauffeur
  return (
    <Card data-testid="paperbon-step-3">
      <CardContent className="p-5 space-y-4">
        <Stepper />
        <div className="space-y-2">
          <Badge className="bg-emerald-600 text-white border-0">Étape 4 / 4</Badge>
          <div className="text-xl font-bold text-slate-900">Votre signature (chauffeur)</div>
          <div className="text-sm text-slate-600">
            Vous attestez que les photos et le nom saisis correspondent à la réalité.
          </div>
          <AudioInstruction text="Signez avec votre doigt, vous attestez que les informations sont correctes" testId="paperbon-step3-audio" />
        </div>
        <div className="border-2 border-dashed border-emerald-300 rounded-lg bg-white">
          <SignatureCanvas
            ref={sigRef}
            penColor="#0f172a"
            canvasProps={{ className: 'w-full h-56 rounded-lg', 'data-testid': 'paperbon-signature-canvas' }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => sigRef.current?.clear()}
                  data-testid="paperbon-clear-sig" className="gap-1.5">
            <Eraser className="w-3.5 h-3.5" /> Effacer
          </Button>
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={() => setStep(2)}
                  data-testid="paperbon-step3-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <Button size="lg" disabled={submitting} onClick={submit}
                  data-testid="paperbon-submit-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-14 px-6 text-base font-bold">
            {submitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Enregistrement…</>)
                       : (<><CheckCircle2 className="w-5 h-5" /> Valider</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaperBonFlow;
